<script setup>
/* global axios */
import { computed, ref, watch } from 'vue';
import { DirectUpload } from 'activestorage';
import { setDirectUploadAuthHeaders } from 'dashboard/helper/directUploadsHelper';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  show: { type: Boolean, default: false },
  accountId: { type: Number, required: true },
  conversationId: { type: Number, required: true },
  inboxId: { type: Number, required: true },
});

const emit = defineEmits(['close', 'select']);
const assets = ref([]);
const loading = ref(false);
const uploading = ref(false);
const search = ref('');
const selectedFile = ref(null);
const name = ref('');
const category = ref('Dental');
const languageCode = ref('en_US');

const filteredAssets = computed(() => {
  const needle = search.value.trim().toLocaleLowerCase();
  if (!needle) return assets.value;

  return assets.value.filter(asset =>
    [asset.name, asset.filename, asset.category, asset.language_code]
      .filter(Boolean)
      .some(value => value.toLocaleLowerCase().includes(needle))
  );
});

const endpoint = computed(
  () => `/api/v1/accounts/${props.accountId}/clinicexpert_media_assets`
);

const fetchAssets = async () => {
  loading.value = true;
  try {
    const { data } = await axios.get(endpoint.value, {
      params: { inbox_id: props.inboxId },
    });
    assets.value = data;
  } catch (error) {
    useAlert(error?.response?.data?.error || 'Media library could not be loaded.');
  } finally {
    loading.value = false;
  }
};

watch(
  () => props.show,
  value => {
    if (value) fetchAssets();
  },
  { immediate: true }
);

const onFileChange = event => {
  selectedFile.value = event.target.files?.[0] || null;
  if (selectedFile.value && !name.value) {
    name.value = selectedFile.value.name;
  }
};

const uploadToLibrary = async () => {
  if (!selectedFile.value || uploading.value) return;

  uploading.value = true;
  const upload = new DirectUpload(
    selectedFile.value,
    `/api/v1/accounts/${props.accountId}/conversations/${props.conversationId}/direct_uploads`,
    {
      directUploadWillCreateBlobWithXHR: xhr => {
        setDirectUploadAuthHeaders(xhr);
      },
    }
  );

  upload.create(async (error, blob) => {
    if (error) {
      useAlert(String(error));
      uploading.value = false;
      return;
    }

    try {
      const { data } = await axios.post(endpoint.value, {
        blob_signed_id: blob.signed_id,
        name: name.value || selectedFile.value.name,
        category: category.value || null,
        language_code: languageCode.value || null,
        inbox_id: props.inboxId,
      });
      assets.value.push(data);
      assets.value.sort((a, b) => a.name.localeCompare(b.name));
      selectedFile.value = null;
      name.value = '';
      useAlert('Media library item added.');
    } catch (requestError) {
      useAlert(
        requestError?.response?.data?.error ||
          'Media library item could not be added.'
      );
    } finally {
      uploading.value = false;
    }
  });
};

const chooseAsset = asset => {
  emit('select', asset);
  emit('close');
};

const iconFor = asset => {
  if (asset.file_type === 'video') return '🎬';
  if (asset.file_type === 'audio') return '🎧';
  return '📄';
};
</script>

<template>
  <div
    v-if="show"
    class="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4"
    @click.self="$emit('close')"
  >
    <div class="flex max-h-[85vh] w-full max-w-3xl flex-col rounded-xl bg-n-solid-2 shadow-xl">
      <div class="flex items-center justify-between border-b border-n-weak px-5 py-4">
        <div>
          <h3 class="text-lg font-semibold text-n-slate-12">Media Library</h3>
          <p class="text-sm text-n-slate-10">Select a reusable image, video or PDF.</p>
        </div>
        <button class="text-xl text-n-slate-10 hover:text-n-slate-12" @click="$emit('close')">×</button>
      </div>

      <div class="border-b border-n-weak p-4">
        <input
          v-model="search"
          type="search"
          placeholder="Search media"
          class="w-full rounded-lg border border-n-weak bg-n-alpha-black2 px-3 py-2 text-sm text-n-slate-12"
        />
      </div>

      <div class="grid flex-1 grid-cols-1 gap-3 overflow-y-auto p-4 sm:grid-cols-2">
        <p v-if="loading" class="col-span-full py-8 text-center text-n-slate-10">Loading…</p>
        <p v-else-if="!filteredAssets.length" class="col-span-full py-8 text-center text-n-slate-10">No media has been added for this inbox.</p>

        <button
          v-for="asset in filteredAssets"
          :key="asset.id"
          type="button"
          class="flex items-center gap-3 rounded-lg border border-n-weak bg-n-solid-1 p-3 text-left hover:border-n-blue-8"
          @click="chooseAsset(asset)"
        >
          <img
            v-if="asset.file_type === 'image'"
            :src="asset.thumb_url"
            class="h-16 w-16 rounded-md object-cover"
          />
          <span v-else class="flex h-16 w-16 items-center justify-center rounded-md bg-n-slate-3 text-3xl">{{ iconFor(asset) }}</span>
          <span class="min-w-0">
            <strong class="block truncate text-sm text-n-slate-12">{{ asset.name }}</strong>
            <span class="block truncate text-xs text-n-slate-10">{{ asset.filename }}</span>
            <span class="mt-1 block text-xs text-n-slate-9">{{ asset.category || 'General' }} · {{ asset.language_code || 'All languages' }}</span>
          </span>
        </button>
      </div>

      <div class="border-t border-n-weak p-4">
        <p class="mb-3 text-sm font-semibold text-n-slate-12">Add to library (administrators)</p>
        <div class="grid gap-2 sm:grid-cols-2">
          <input v-model="name" type="text" placeholder="Display name" class="rounded-md border border-n-weak bg-n-solid-1 px-3 py-2 text-sm" />
          <input v-model="category" type="text" placeholder="Category" class="rounded-md border border-n-weak bg-n-solid-1 px-3 py-2 text-sm" />
          <input v-model="languageCode" type="text" placeholder="Language, e.g. en_US" class="rounded-md border border border-n-weak bg-n-solid-1 px-3 py-2 text-sm" />
          <input type="file" accept="image/*,video/*,audio/*,application/pdf" class="text-sm text-n-slate-11" @change="onFileChange" />
        </div>
        <button
          type="button"
          class="mt-3 rounded-md bg-n-blue-9 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
          :disabled="!selectedFile || uploading"
          @click="uploadToLibrary"
        >
          {{ uploading ? 'Uploading…' : 'Add to Media Library' }}
        </button>
      </div>
    </div>
  </div>
</template>
